import { useCallback } from "react";
import {
  usePaymentsClientContext,
  usePaymentsClientStateContext,
} from "../../../../../../contexts/payments-client-context";
import {
  payment3_commonpb,
  payment3_paymentmethodpb,
} from "@ftdr/payment3_paymentmethod_coordinator-js-client";
import {
  AgregatedProcessPaymentResponse,
  GeneralStatusValue,
  ProcessPaymentPaypalRequestData,
  SuccessTypeValue,
} from "../../../../types";
import { usePaymentContext } from "../../../../../../contexts/payment-context";

type HandleCreatePaymentMethod = (
  requestData: ProcessPaymentPaypalRequestData | undefined,
  registrationID: string
) => Promise<AgregatedProcessPaymentResponse>;

export const useHandleCreatePaymentMethod = (): HandleCreatePaymentMethod => {
  const {
    createPaymentMethod,
    applicationState: { applicationName, owner, formsSetup, customerId },
    cashFlow,
  } = usePaymentsClientContext();
  const { state } = usePaymentsClientStateContext();
  const { brand, subbrand } = usePaymentContext();

  const formBrand = formsSetup?.braintreeForm?.brand;

  const handleCreatePaymentMethod = useCallback<HandleCreatePaymentMethod>(
    async (requestData, registrationID) => {
      const createPaymentMethodResult = await createPaymentMethod({
        registrationID,
        paymentMethod: {
          brand: brand || formBrand,
          ...(subbrand ? { subbrand } : {}),
          source: applicationName ? { name: applicationName } : undefined,
          owner: owner ? owner : requestData?.paypal?.owner,
          includedInWallet: requestData?.paypal?.includedInWallet,
          processor: {
            processor: payment3_commonpb.PaymentProcessor.Braintree,
            braintree: {
              braintreeCustomerID:
                state.paypalData?.onApproveBraintreeData?.paymentID,
              payPalPayerID: state.paypalData?.onApproveBraintreeData?.payerID,
              paymentMethodToken:
                state.paypalData?.onApproveBraintreeData?.paymentToken,
              nonce: state.paypalData?.braintreeTokenizePayloadData?.nonce,
            },
          },
          details: {
            type: payment3_paymentmethodpb.PaymentMethodType.PayPal,
            cashFlow,
            paypal: {
              email:
                state.paypalData?.braintreeTokenizePayloadData?.details?.email,
              firstName:
                state.paypalData?.braintreeTokenizePayloadData?.details
                  ?.firstName,
              lastName:
                state.paypalData?.braintreeTokenizePayloadData?.details
                  ?.lastName,
            },
          },
          ...(customerId && {
            disbursementDetails: { customer: { customerID: customerId } },
          }),
        },
      });

      if (
        createPaymentMethodResult?.success?.type ===
        SuccessTypeValue.PAYPAL_SUCCESS
      ) {
        return {
          paypal: {
            paypalSuccessResponse: { ...createPaymentMethodResult?.result },
            type: createPaymentMethodResult.success.type,
          },
          generalStatus: GeneralStatusValue.PAYMENT_SUCCESS,
        };
      } else {
        return {
          paypal: {
            ...createPaymentMethodResult?.error,
          },
          generalStatus: GeneralStatusValue.PAYMENT_FAILED,
        };
      }
    },
    [
      applicationName,
      brand,
      cashFlow,
      createPaymentMethod,
      customerId,
      formBrand,
      owner,
      state.paypalData?.braintreeTokenizePayloadData?.nonce,
      state.paypalData?.onApproveBraintreeData?.payerID,
      state.paypalData?.onApproveBraintreeData?.paymentID,
      state.paypalData?.onApproveBraintreeData?.paymentToken,
      state.paypalData?.braintreeTokenizePayloadData?.details?.email,
      state.paypalData?.braintreeTokenizePayloadData?.details?.firstName,
      state.paypalData?.braintreeTokenizePayloadData?.details?.lastName,
      subbrand,
    ]
  );

  return handleCreatePaymentMethod;
};
